/* Click-to-zoom, drag-to-pan viewer for the product gallery's main image.
 *
 * Dawn ships two zoom modes and neither suits a rug shop: "lightbox" only
 * reopens the same image at screen size (no closer look at pile or weave), and
 * "hover" (magnify.js) binds once at page load so it dies after a variant swap,
 * and does its maths from the <img> box -- which is wrong here because this
 * theme letterboxes the photo with object-fit: contain.
 *
 * This replaces both:
 *   - click the photo to zoom in at the point clicked, click again to zoom out;
 *   - while zoomed, drag to pan around the parts now outside the frame;
 *   - the magnified image is clipped to the photo's own painted rectangle, so
 *     it never bleeds into the letterbox margins beside a portrait rug;
 *   - a 2400px source is preloaded and swapped in, so magnifying reveals real
 *     detail instead of enlarging the same pixels;
 *   - delegated from the gallery element, so it survives Dawn replacing the
 *     media on a variant change.
 *
 * Desktop pointers only. Touch taps and keyboard activation fall through to
 * Dawn's full-screen lightbox, which is the better answer on both.
 */
if (!window.rugImageZoomLoaded) {
  window.rugImageZoomLoaded = true;

  (() => {
    const ZOOM = 2.5;
    const SOURCE_WIDTH = 2400;
    // Below this a mousedown/mouseup pair is a click (toggle the zoom); above
    // it, the pointer was dragging and the trailing click must be swallowed.
    const DRAG_SLOP = 4;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const states = new WeakMap();

    // Shopify CDN URLs carry the rendition width as a query param. Swapping it
    // gives a sharper source without a second round trip to Liquid.
    function highResSource(img) {
      const src = img.currentSrc || img.src;
      if (!src) return null;
      try {
        const url = new URL(src, window.location.href);
        if (!url.searchParams.has('width')) return src;
        if (Number(url.searchParams.get('width')) >= SOURCE_WIDTH) return src;
        url.searchParams.set('width', String(SOURCE_WIDTH));
        return url.toString();
      } catch {
        return src;
      }
    }

    // Where the photo is actually painted inside its box, and at what scale.
    // Measured against the .media wrapper rather than .product-media-container
    // so the media border (global-media-settings) can't offset the maths.
    function paintedGeometry(img) {
      const frame = img.parentElement;
      if (!frame) return null;

      const box = frame.getBoundingClientRect();
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh || !box.width || !box.height) return null;

      const fit = getComputedStyle(img).objectFit;
      const ratio =
        fit === 'cover' ? Math.max(box.width / nw, box.height / nh) : Math.min(box.width / nw, box.height / nh);

      const width = nw * ratio;
      const height = nh * ratio;
      return {
        frame,
        width,
        height,
        left: (box.width - width) / 2,
        top: (box.height - height) / 2,
      };
    }

    function lensFor(frame) {
      let lens = frame.querySelector(':scope > .rug-zoom-lens');
      if (!lens) {
        lens = document.createElement('div');
        lens.className = 'rug-zoom-lens';
        lens.setAttribute('aria-hidden', 'true');
        frame.appendChild(lens);
      }
      return lens;
    }

    // The already-decoded image goes in first so the zoom is instant, then the
    // 2400px rendition swaps in behind it once decoded. Without the two steps
    // the first click flashes an empty lens while the big file downloads.
    function setSource(lens, img) {
      const current = img.currentSrc || img.src;
      if (!current) return;

      if (lens.dataset.base !== current) {
        lens.dataset.base = current;
        delete lens.dataset.hires;
        lens.style.backgroundImage = `url("${current}")`;
      }

      const hires = highResSource(img);
      if (!hires || hires === current || lens.dataset.hires === hires) return;

      const preload = new Image();
      preload.addEventListener('load', () => {
        // The gallery may have moved on to another image by the time this
        // arrives; only apply it if the lens is still showing the same photo.
        if (lens.dataset.base !== current) return;
        lens.dataset.hires = hires;
        lens.style.backgroundImage = `url("${hires}")`;
      });
      preload.src = hires;
    }

    // Keep the magnified image covering the lens: never past its leading edge,
    // never short of its trailing one.
    function clamp(value, size, frameSize) {
      if (size <= frameSize) return (frameSize - size) / 2;
      return Math.min(0, Math.max(frameSize - size, value));
    }

    function applyOffset(state, x, y) {
      state.x = clamp(x, state.zoomedWidth, state.geometry.width);
      state.y = clamp(y, state.zoomedHeight, state.geometry.height);
      state.lens.style.backgroundPosition = `${state.x}px ${state.y}px`;
    }

    function zoomIn(container, img, event) {
      const geometry = paintedGeometry(img);
      if (!geometry) return;

      const lens = lensFor(geometry.frame);
      setSource(lens, img);

      lens.style.left = `${geometry.left}px`;
      lens.style.top = `${geometry.top}px`;
      lens.style.width = `${geometry.width}px`;
      lens.style.height = `${geometry.height}px`;

      const zoomedWidth = geometry.width * ZOOM;
      const zoomedHeight = geometry.height * ZOOM;
      lens.style.backgroundSize = `${zoomedWidth}px ${zoomedHeight}px`;

      const state = { container, lens, geometry, zoomedWidth, zoomedHeight, x: 0, y: 0, dragging: false, moved: false };
      states.set(container, state);

      // Zoom about the point clicked, so the detail the shopper aimed at is the
      // detail they get rather than the middle of the rug.
      const frameBox = geometry.frame.getBoundingClientRect();
      const pointX = event.clientX - frameBox.left - geometry.left;
      const pointY = event.clientY - frameBox.top - geometry.top;
      applyOffset(state, pointX - pointX * ZOOM, pointY - pointY * ZOOM);

      container.classList.add('rug-zoom-on');
    }

    function zoomOut(container) {
      const state = states.get(container);
      container.classList.remove('rug-zoom-on', 'rug-zoom-dragging');
      if (state) state.dragging = false;
      states.delete(container);
    }

    function zoomOutAll() {
      document.querySelectorAll('.product-media-container.rug-zoom-on').forEach(zoomOut);
    }

    // Only the main viewer image, never a thumbnail or a video poster.
    function mainImage(container) {
      const img = container.querySelector('.product__media img');
      return img && img.complete && img.naturalWidth ? img : null;
    }

    function imageContainer(target) {
      const container = target.closest?.('.product-media-container.media-type-image');
      if (!container || container.closest('.thumbnail-list')) return null;
      return container;
    }

    function bind(gallery) {
      if (gallery.dataset.rugZoomBound === 'true') return;
      gallery.dataset.rugZoomBound = 'true';

      // Capture phase, so the click never reaches <modal-opener>'s own handler
      // on the button and the full-screen modal stays shut while zooming.
      gallery.addEventListener(
        'click',
        (event) => {
          const container = imageContainer(event.target);
          if (!container) {
            // A thumbnail or arrow: the main image is about to change, so the
            // zoom that belonged to the old one has to go.
            zoomOutAll();
            return;
          }
          if (!canHover.matches) return;
          // detail 0 means Enter/Space on the focused button. Keyboard users
          // have nowhere to aim and nothing to drag with, so let them have the
          // lightbox instead.
          if (event.detail === 0) return;

          event.preventDefault();
          event.stopPropagation();

          const state = states.get(container);
          if (state?.moved) {
            state.moved = false;
            return;
          }
          if (state) {
            zoomOut(container);
            return;
          }

          const img = mainImage(container);
          if (img) zoomIn(container, img, event);
        },
        true
      );

      gallery.addEventListener('mousedown', (event) => {
        if (event.button !== 0) return;
        const container = imageContainer(event.target);
        const state = container && states.get(container);
        if (!state) return;

        state.dragging = true;
        state.moved = false;
        state.startX = event.clientX;
        state.startY = event.clientY;
        state.originX = state.x;
        state.originY = state.y;
        container.classList.add('rug-zoom-dragging');
        // Suppress the native image drag and the text selection that otherwise
        // follows the pointer out of the gallery.
        event.preventDefault();
      });

      gallery.addEventListener('dragstart', (event) => {
        if (imageContainer(event.target)) event.preventDefault();
      });
    }

    // On the document rather than the gallery: a drag that leaves the image --
    // which is normal when panning to an edge -- must keep panning, and must
    // still end wherever the button is released.
    document.addEventListener('mousemove', (event) => {
      document.querySelectorAll('.product-media-container.rug-zoom-dragging').forEach((container) => {
        const state = states.get(container);
        if (!state?.dragging) return;

        const dx = event.clientX - state.startX;
        const dy = event.clientY - state.startY;
        if (Math.abs(dx) > DRAG_SLOP || Math.abs(dy) > DRAG_SLOP) state.moved = true;
        applyOffset(state, state.originX + dx, state.originY + dy);
      });
    });

    document.addEventListener('mouseup', () => {
      document.querySelectorAll('.product-media-container.rug-zoom-dragging').forEach((container) => {
        const state = states.get(container);
        if (state) state.dragging = false;
        container.classList.remove('rug-zoom-dragging');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') zoomOutAll();
    });

    // Every measurement above is in pixels taken at zoom time, so a resize or
    // an orientation change invalidates all of them.
    window.addEventListener('resize', zoomOutAll);

    function init() {
      document.querySelectorAll('media-gallery').forEach(bind);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    // Dawn swaps whole sections in the theme editor and on variant change.
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('product-info:loaded', () => {
      zoomOutAll();
      init();
    });
  })();
}
